import {
  afterNextRender,
  Component,
  Injector,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ErreurIntervention, PhotoIntervention } from '../../Services/intervention-api';
import { ErreurApi, messageErreurApi } from '../../Services/api-error';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { InterventionPhotos } from '../intervention-photos/intervention-photos';
import { FormField, disabled, form, required } from '@angular/forms/signals';
import { CONFIG_ETATS_TICKET, TicketState } from '../../Services/ticket-state';
import { jourPlanification } from '../../Services/intervention-dates';
import {
  BrouillonIntervention,
  ContexteIntervention,
  DroitsInterventions,
  InterventionEditable,
  NatureIntervention,
  NOMBRE_MAX_PHOTOS_INTERVENTION,
  OptionsIntervention,
  SaisieIntervention,
  TAILLE_MAX_PHOTO_INTERVENTION,
  TYPES_PHOTOS_INTERVENTION,
  UtilisateurIntervention,
  creerBrouillonIntervention,
  peutModifierBon,
  preparerSaisieIntervention,
  validerSaisieIntervention,
} from '../../Services/intervention-edition';

/** Traduit les clés de validation de l’API en libellés compréhensibles dans le résumé d’erreurs. */
const LIBELLES_CHAMPS: Record<
  keyof BrouillonIntervention | 'photos' | 'donnees' | 'fuseau',
  string
> = {
  categorie: 'Type d’intervention',
  installationId: 'Centrale',
  ticketId: 'Ticket',
  description: 'Description de l’intervention',
  date: 'Date de l’intervention',
  debut: 'Heure de début',
  fin: 'Heure de fin',
  problemeResolu: 'Problème résolu',
  photos: 'Photos',
  donnees: 'Données du formulaire',
  fuseau: 'Fuseau horaire',
};

/** Formulaire indépendant du transport. Le parent fournit les données autorisées et la sauvegarde. */
@Component({
  selector: 'app-intervention-formulaire',
  imports: [FormField, MatAutocompleteModule, InterventionPhotos],
  templateUrl: './intervention-formulaire.html',
  styleUrl: './intervention-formulaire.css',
})
export class InterventionFormulaire {
  // Des identifiants propres à chaque instance relient les aides et les erreurs à leurs champs.
  private static prochainId = 0;
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly resumeErreurs = viewChild<ElementRef<HTMLElement>>('resumeErreurs');

  readonly nature = input.required<NatureIntervention>();
  readonly droits = input.required<DroitsInterventions>();
  readonly utilisateur = input.required<UtilisateurIntervention | null>();
  readonly options = input.required<OptionsIntervention>();
  readonly contexte = input<ContexteIntervention>({});
  readonly edition = input<InterventionEditable | null>(null);
  readonly transformation = input(false);
  readonly autorisationServeur = input(true);
  readonly photosExistantes = input<readonly PhotoIntervention[]>([]);
  readonly chargement = input(false);
  readonly erreurChargement = input('');
  readonly sauvegardeDisponible = input(true);
  /** Doit rejeter si l'API ne confirme pas la sauvegarde, y compris avec un HTTP 200 en erreur. */
  readonly enregistrer = input.required<(saisie: SaisieIntervention) => Promise<void>>();

  readonly installationChange = output<string>();
  readonly enregistre = output<SaisieIntervention>();
  readonly annuler = output<void>();
  readonly recharger = output<void>();

  private readonly prefixeId = `intervention-formulaire-${InterventionFormulaire.prochainId++}`;
  readonly idChargementTickets = `${this.prefixeId}-chargement-tickets`;
  readonly idErreurChargementTickets = `${this.prefixeId}-erreur-chargement-tickets`;
  readonly typesPhotosAcceptes = TYPES_PHOTOS_INTERVENTION.join(',');
  private readonly instantValidation = signal(new Date());
  readonly dateMinPlanification = computed(() => jourPlanification(this.instantValidation()));

  private readonly identite = computed(() => {
    const utilisateur = this.utilisateur();
    return utilisateur ? String(utilisateur.id ?? 'sans-identifiant') : null;
  });
  // Cette identité de demande réinitialise le brouillon si le contexte ou l’utilisateur change.
  // Elle sert aussi à ignorer une sauvegarde qui se terminerait pour une ancienne demande.
  private readonly demande = computed(() => ({
    nature: this.nature(),
    edition: this.edition(),
    contexte: this.contexte(),
    identite: this.identite(),
  }));
  private readonly valeursInitiales = computed(
    () => this.demande().edition?.valeurs ?? creerBrouillonIntervention(this.demande().contexte),
  );
  readonly modele = linkedSignal(() => {
    // Même si les valeurs initiales sont identiques, une nouvelle demande réinitialise le brouillon.
    this.demande();
    return { ...this.valeursInitiales() };
  });

  // Ces états appartiennent à une seule demande, pas à toute la durée de vie du composant.
  readonly photos = linkedSignal<File[]>(() => {
    this.demande();
    return [];
  });
  readonly tentative = linkedSignal(() => {
    this.demande();
    return false;
  });
  readonly envoi = linkedSignal(() => {
    this.demande();
    return false;
  });
  readonly erreurPhotos = linkedSignal(() => {
    this.demande();
    return '';
  });
  readonly termine = linkedSignal(() => {
    this.demande();
    return false;
  });
  /** Signale au parent les changements de valeurs ou l’ajout de photos à protéger à la fermeture. */
  readonly modifie = computed(() => {
    const initial = this.valeursInitiales();
    return (
      this.photos().length > 0 ||
      Object.entries(this.modele()).some(
        ([champ, valeur]) => valeur !== initial[champ as keyof BrouillonIntervention],
      )
    );
  });

  /** Une création depuis un ticket conserve sa centrale et son rattachement. */
  readonly contexteVerrouille = computed(
    () => this.transformation() || (!this.edition() && !!this.contexte().ticketId),
  );
  readonly ticketRequis = computed(
    () => this.nature() === 'bon' || this.modele().categorie === 'curative',
  );
  /** Croise l’autorisation relue du serveur avec les droits locaux et l’auteur du bon. */
  readonly autorise = computed(() => {
    const edition = this.edition();
    if (
      !this.autorisationServeur() ||
      !this.utilisateur() ||
      (edition && edition.nature !== this.nature())
    )
      return false;
    if (edition) {
      if (this.transformation()) return this.droits().modifierPlanification;
      return edition.nature === 'bon'
        ? peutModifierBon(
            this.utilisateur(),
            edition.auteurId,
            Number(edition.valeurs.installationId),
          )
        : this.droits().modifierPlanification;
    }
    // Tout utilisateur connecté peut créer un bon, indépendamment de son profil.
    return this.nature() === 'bon' || this.droits().planifier;
  });
  // Une panne des tickets ne doit pas empêcher de corriger la centrale ou le type d’intervention.
  readonly saisieBloquee = computed(() => this.envoi() || this.termine() || !this.autorise());
  readonly ticketsIndisponibles = computed(() => this.chargement() || !!this.erreurChargement());
  // La sauvegarde reste interdite tant que la liste requise est incomplète.
  readonly indisponible = computed(
    () => this.saisieBloquee() || (this.ticketRequis() && this.ticketsIndisponibles()),
  );
  // Signal Forms pilote les contrôles ; la validation métier commune vérifie aussi leurs relations.
  readonly formulaire = form(this.modele, (schema) => {
    disabled(schema.categorie, () => this.contexteVerrouille());
    required(schema.installationId);
    required(schema.ticketId, {
      when: () => this.nature() === 'bon' || this.modele().categorie === 'curative',
    });
    required(schema.description);
    required(schema.date, { when: () => this.nature() === 'planification' });
  });

  // L’égalité explicite préserve le texte saisi quand les listes changent sans changer la sélection.
  private readonly contexteRechercheInstallation = computed(
    () => ({
      id: this.modele().installationId,
      libelle: this.libelleInstallation(this.modele().installationId),
    }),
    { equal: (a, b) => a.id === b.id && a.libelle === b.libelle },
  );
  readonly rechercheInstallation = linkedSignal(() => this.contexteRechercheInstallation().libelle);
  /** Recherche par nom sans accents ou identifiant ; une sélection conserve la liste complète. */
  readonly installationsFiltrees = computed(() => {
    const recherche = this.normaliserRecherche(this.rechercheInstallation());
    if (this.modele().installationId) return this.options().installations;
    return this.options().installations.filter(
      (item) =>
        this.normaliserRecherche(item.nom).includes(recherche) ||
        String(item.id).includes(recherche),
    );
  });
  /** Résout l’identifiant choisi en texte affiché par l’autocomplétion. */
  readonly libelleInstallation = (id: string): string =>
    this.options().installations.find((item) => String(item.id) === id)?.nom ?? '';

  // Les libellés historiques peuvent contenir du HTML ; la recherche et l’affichage utilisent du texte.
  private readonly ticketsTexte = computed(() =>
    this.options().tickets.map((ticket) => ({
      ...ticket,
      libelle: this.texteDepuisHtml(ticket.libelle),
    })),
  );
  readonly tickets = computed(() =>
    this.ticketsTexte().filter(
      (ticket) => ticket.installationId === Number(this.modele().installationId),
    ),
  );
  private readonly contexteRechercheTicket = computed(
    () => ({
      installationId: this.modele().installationId,
      categorie: this.modele().categorie,
      ticketId: this.modele().ticketId,
      libelle: this.libelleTicket(this.modele().ticketId),
    }),
    {
      equal: (a, b) =>
        a.installationId === b.installationId &&
        a.categorie === b.categorie &&
        a.ticketId === b.ticketId &&
        a.libelle === b.libelle,
    },
  );
  readonly rechercheTicket = linkedSignal(() => this.contexteRechercheTicket().libelle);
  /** Affiche numéro et description du ticket sélectionné, uniquement dans la centrale courante. */
  readonly libelleTicket = (id: string): string => {
    const ticket = this.tickets().find((item) => String(item.id) === id);
    return ticket ? `${ticket.numero} — ${ticket.libelle}` : '';
  };
  /** Recherche sans accents dans le numéro et le libellé, sans exclure les tickets résolus. */
  readonly ticketsFiltres = computed(() => {
    const disponibles = this.tickets();
    if (this.modele().ticketId) return disponibles;
    const recherche = this.normaliserRecherche(this.rechercheTicket());
    return disponibles.filter((ticket) =>
      this.normaliserRecherche(`${ticket.numero} ${ticket.libelle}`).includes(recherche),
    );
  });
  /** Produit les optgroups dans l’ordre des états, avec un groupe de repli pour les états inconnus. */
  readonly groupesTickets = computed(() => {
    const tickets = this.ticketsFiltres();
    return [
      ...Object.values(TicketState).map((etat) => ({
        etat,
        libelle: etat === TicketState.resolu ? 'Résolus' : CONFIG_ETATS_TICKET[etat].label,
        tickets: tickets.filter((ticket) => ticket.etat === etat),
      })),
      { etat: null, libelle: 'Autres états', tickets: tickets.filter((ticket) => !ticket.etat) },
    ].filter((groupe) => groupe.tickets.length > 0);
  });

  readonly erreurs = computed(() =>
    validerSaisieIntervention(
      this.nature(),
      this.modele(),
      this.options(),
      this.instantValidation(),
    ),
  );
  readonly erreurEnvoi = linkedSignal(() => {
    this.modele();
    this.photos();
    return '';
  });
  // Une correction de champ ou de photo invalide les erreurs du précédent envoi.
  readonly erreursServeur = linkedSignal<Record<string, string>>(() => {
    this.modele();
    this.photos();
    return {};
  });
  /** Associe les erreurs de fichiers à leur nom et conserve aussi les champs inconnus de l’API. */
  readonly erreursServeurAffichees = computed(() =>
    Object.entries(this.erreursServeur()).map(([champ, message]) => ({
      champ,
      message,
      ...this.decrireChampErreur(champ),
    })),
  );
  readonly erreurPhotosServeur = computed(() =>
    this.erreursServeurAffichees()
      .filter((erreur) => erreur.photo)
      .map((erreur) => `${erreur.libelle} : ${erreur.message}`)
      .join(' '),
  );
  readonly detailsErreurEnvoi = linkedSignal<string[]>(() => {
    this.modele();
    this.photos();
    return [];
  });
  // Évite de répéter le même message dans l’alerte générale, les champs et les détails.
  readonly detailsErreurAffiches = computed(() => {
    const dejaAffiches = new Set([this.erreurEnvoi(), ...Object.values(this.erreursServeur())]);
    return [...new Set(this.detailsErreurEnvoi().map((detail) => detail.trim()))].filter(
      (detail) => detail && !dejaAffiches.has(detail),
    );
  });
  /** Associe le chargement local et l’éventuelle erreur au champ Ticket pour les lecteurs d’écran. */
  readonly descriptionTicket = computed(
    () =>
      [
        this.chargement() ? this.idChargementTickets : null,
        this.erreurChargement() ? this.idErreurChargementTickets : null,
        this.erreurChamp('ticketId') ? this.idErreur('ticketId') : null,
      ]
        .filter(Boolean)
        .join(' ') || null,
  );

  readonly titre = computed(() => {
    if (this.transformation()) return 'Transformer en bon d’intervention';
    if (this.edition() && this.nature() === 'planification') return 'Modifier une planification';
    const action = this.edition() ? 'Modifier' : this.nature() === 'bon' ? 'Créer' : 'Planifier';
    return `${action} ${this.nature() === 'bon' ? 'un bon d’intervention' : 'une intervention'}`;
  });
  // Données temporaires de présentation : ne font pas partie de la saisie envoyée à l’API.
  readonly intervenantsDemonstration = [
    { icone: 'person', libelle: 'Camille Martin — Solis Maintenance' },
    { icone: 'business', libelle: 'Énergie Services' },
  ] as const;
  readonly afficherIntervenants = computed(
    () =>
      this.nature() === 'planification' &&
      this.modele().categorie === 'curative' &&
      this.tickets().some((ticket) => String(ticket.id) === this.modele().ticketId),
  );

  /** Identifiant utilisé à la fois par le message et par aria-describedby. */
  idErreur(champ: keyof BrouillonIntervention | 'photos'): string {
    return `${this.prefixeId}-${champ}-erreur`;
  }

  /** Priorité au refus serveur ; les erreurs locales apparaissent après la première soumission. */
  erreurChamp(champ: keyof BrouillonIntervention): string | null {
    if (this.erreursServeur()[champ]) return this.erreursServeur()[champ];
    return this.tentative()
      ? (this.erreurs().find((e) => e.champ === champ)?.message ?? null)
      : null;
  }

  /** Une saisie libre annule le choix précédent et son ticket avant de filtrer les centrales. */
  rechercherInstallation(texte: string): void {
    if (this.saisieBloquee() || this.contexteVerrouille()) return;
    if (this.modele().installationId) this.changerInstallation('');
    this.rechercheInstallation.set(texte);
  }

  /** Dissocie le texte de recherche de l’identifiant validé : taper ne sélectionne pas un ticket. */
  rechercherTicket(texte: string): void {
    if (this.indisponible() || this.contexteVerrouille()) return;
    if (this.modele().ticketId) this.changerTicket('');
    this.rechercheTicket.set(texte);
  }

  /** Applique le choix et ferme l’autocomplétion malgré le retour de focus de Material. */
  selectionnerOption(
    champ: 'installation' | 'ticket',
    id: string,
    liste: MatAutocompleteTrigger,
  ): void {
    // Fermer avant de modifier les choix, puis après leur rendu : Material
    // rend le focus au champ pendant la sélection et peut rouvrir l’overlay.
    liste.closePanel();
    if (champ === 'installation') this.changerInstallation(id);
    else this.changerTicket(id);
    afterNextRender(() => liste.closePanel(), { injector: this.injector });
  }

  /** Efface le ticket dépendant et demande au parent de charger ceux de la nouvelle centrale. */
  changerInstallation(id: string): void {
    if (this.saisieBloquee() || this.contexteVerrouille() || id === this.modele().installationId)
      return;
    this.modele.update((m) => ({ ...m, installationId: id, ticketId: '' }));
    this.installationChange.emit(id);
  }

  /** Met à jour le rattachement, sauf si le contexte est imposé ou le formulaire indisponible. */
  changerTicket(id: string): void {
    if (this.indisponible() || this.contexteVerrouille()) return;
    this.modele.update((m) => ({ ...m, ticketId: id }));
  }

  /** Retire le ticket en préventif et réévalue avec le parent la nécessité de charger les tickets. */
  changerCategorie(categorie: string): void {
    if (this.saisieBloquee() || this.contexteVerrouille()) return;
    if (categorie !== 'curative' && categorie !== 'preventive') return;
    this.modele.update((m) => ({
      ...m,
      categorie,
      ticketId: categorie === 'preventive' ? '' : m.ticketId,
    }));
    this.installationChange.emit(this.modele().installationId);
  }

  /** Valide le lot entier avant ajout : JPEG/PNG/WebP, 10 Mo par fichier et 10 photos au total. */
  ajouterPhotos(event: Event): void {
    if (this.saisieBloquee()) return;
    const champ = event.target as HTMLInputElement;
    const fichiers = Array.from(champ.files ?? []);
    if (fichiers.some((fichier) => !TYPES_PHOTOS_INTERVENTION.includes(fichier.type))) {
      this.erreurPhotos.set(
        'Sélectionnez uniquement des images JPEG, PNG ou WebP. Aucun fichier n’a été ajouté.',
      );
    } else if (fichiers.some((fichier) => fichier.size > TAILLE_MAX_PHOTO_INTERVENTION)) {
      this.erreurPhotos.set('Chaque photo doit peser au maximum 10 Mo.');
    } else if (
      this.photosExistantes().length + this.photos().length + fichiers.length >
      NOMBRE_MAX_PHOTOS_INTERVENTION
    ) {
      this.erreurPhotos.set('Un bon peut contenir au maximum 10 photos.');
    } else {
      this.erreurPhotos.set('');
      this.photos.update((photos) => [...photos, ...fichiers]);
    }
    // Permet de sélectionner à nouveau le même fichier, notamment après un refus.
    champ.value = '';
  }

  /** Retire uniquement un fichier du brouillon ; les photos déjà enregistrées restent inchangées. */
  retirerPhoto(index: number): void {
    if (this.saisieBloquee()) return;
    this.photos.update((photos) => photos.filter((_, i) => i !== index));
    this.erreurPhotos.set('');
  }

  /**
   * Valide, dirige le focus vers le premier champ invalide puis délègue la sauvegarde au parent.
   * Un échec conserve la saisie et les fichiers ; une réponse obsolète ne modifie plus le formulaire.
   */
  async soumettre(event: Event): Promise<void> {
    event.preventDefault();
    if (this.indisponible() || !this.sauvegardeDisponible()) return;
    // Une fenêtre restée ouverte peut contenir un horaire devenu passé depuis la dernière saisie.
    this.instantValidation.set(new Date());
    this.tentative.set(true);
    this.erreurEnvoi.set('');
    this.erreursServeur.set({});
    this.detailsErreurEnvoi.set([]);
    if (this.erreurs().length) {
      const champ = this.erreurs()[0].champ;
      this.element.nativeElement.querySelector<HTMLElement>(`[data-champ="${champ}"]`)?.focus();
      return;
    }
    const saisie = preparerSaisieIntervention(
      this.nature(),
      this.modele(),
      this.options(),
      this.edition()?.id ?? null,
      this.photos(),
      this.instantValidation(),
    );
    // Capturer la demande avant l’attente protège contre un changement de contexte ou d’identité.
    const demande = this.demande();
    const estActif = () => !this.destroyRef.destroyed && demande === this.demande();
    this.envoi.set(true);
    try {
      await this.enregistrer()(saisie);
      if (!estActif()) return;
      this.termine.set(true);
      this.enregistre.emit(saisie);
    } catch (erreur) {
      if (estActif()) {
        if (erreur instanceof ErreurIntervention) this.erreursServeur.set(erreur.champs);
        if (erreur instanceof ErreurApi && erreur.details) {
          this.detailsErreurEnvoi.set(
            Array.isArray(erreur.details) ? erreur.details : [erreur.details],
          );
        }
        this.erreurEnvoi.set(
          messageErreurApi(
            erreur,
            'L’enregistrement n’a pas été confirmé. Votre saisie est conservée. Vérifiez les interventions avant de réessayer.',
          ),
        );
        // Le résumé reste monté pendant l'envoi, pour pouvoir lui rendre le focus.
        this.resumeErreurs()?.nativeElement.focus();
      }
    } finally {
      if (estActif()) this.envoi.set(false);
    }
  }

  /** Applique les mêmes règles de recherche aux centrales et aux tickets. */
  private normaliserRecherche(texte: string): string {
    return texte
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('fr')
      .trim();
  }

  /** Résout une clé de validation en champ visible ou en nom de fichier du multipart envoyé. */
  private decrireChampErreur(champ: string): { libelle: string; photo: boolean } {
    const photo = /^photos(?:\.(\d+)|\[(\d*)\])?$/.exec(champ);
    if (!photo) {
      return {
        libelle: Object.hasOwn(LIBELLES_CHAMPS, champ)
          ? LIBELLES_CHAMPS[champ as keyof typeof LIBELLES_CHAMPS]
          : `Champ « ${champ} »`,
        photo: false,
      };
    }
    // Les indices du multipart désignent les nouvelles photos, dans l’ordre d’envoi.
    const index = photo[1] ?? photo[2];
    if (!index) return { libelle: 'Photos', photo: true };
    const fichier = this.photos()[Number(index)];
    return {
      libelle: fichier ? `Photo « ${fichier.name} »` : `Photo n° ${Number(index) + 1}`,
      photo: true,
    };
  }

  /** Extrait un libellé lisible en décodant les entités et en séparant les blocs de texte. */
  private texteDepuisHtml(html: string): string {
    // Le template reste détaché : aucun HTML reçu de l’API n’est affiché
    // ni exécuté. Le parseur décode aussi les entités (&nbsp;, &amp;…).
    const template = this.document.createElement('template');
    template.innerHTML = html;
    template.content
      .querySelectorAll('script, style, template, noscript, iframe, object')
      .forEach((element) => element.remove());
    template.content.querySelectorAll('br').forEach((element) => element.replaceWith(' '));
    template.content.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((element) => {
      element.prepend(' ');
      element.append(' ');
    });
    return (template.content.textContent ?? '').replace(/\s+/g, ' ').trim();
  }
}
